import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTickets, useTicketDetail, useMessages, useCallHistory, useTicketActions } from '../hooks/useTickets';
import useTwilioVoice from '../hooks/useTwilioVoice';
import { TicketStatus, TicketPriority, Message } from '../types/support';
import SupportTicketList, { TicketFilters } from '../components/support/SupportTicketList';
import SupportTicketDetail from '../components/support/SupportTicketDetail';
import IncomingCallModal from '../components/support/IncomingCallModal';
import OnlineAgentsIndicator from '../components/support/OnlineAgentsIndicator';
import { showToast } from '../utils/helpers';
import { supportService, CallerLookupResponse } from '../services/support';

const SupportDashboard: React.FC = () => {
  // Ticket selection state
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  
  // Filter state
  const [filters, setFilters] = useState<TicketFilters>({});
  
  // Mobile state
  const [showDetail, setShowDetail] = useState(false);

  // Caller lookup state for incoming calls
  const [callerInfo, setCallerInfo] = useState<CallerLookupResponse | null>(null);
  const [callerLookupLoading, setCallerLookupLoading] = useState(false);

  // Twilio Voice hook
  const {
    isReady: isVoiceReady,
    isConfigured: isVoiceConfigured,
    callContext,
    incomingCall,
    agentIdentity,
    onlineAgents,
    makeCall,
    acceptCall,
    rejectCall,
    hangup,
    toggleMute,
  } = useTwilioVoice();

  // Look up caller info when an incoming call arrives
  useEffect(() => {
    const lookupCaller = async () => {
      if (callContext.state === 'incoming' && callContext.callerId) {
        setCallerLookupLoading(true);
        try {
          const info = await supportService.lookupCallerByPhone(callContext.callerId);
          setCallerInfo(info);
          
          // If caller has open tickets, show a toast and auto-select the first one
          if (info.found && info.openTickets.length > 0) {
            showToast('info', 
              `Caller ${info.user?.name || 'Unknown'} has ${info.openTickets.length} open ticket(s)`
            );
          } else if (info.found) {
            showToast('info', `Incoming call from ${info.user?.name || 'known customer'}`);
          }
        } catch (error) {
          console.error('Failed to lookup caller:', error);
          // Don't show error toast - caller may be unknown and that's OK
        } finally {
          setCallerLookupLoading(false);
        }
      } else if (callContext.state === 'idle') {
        // Clear caller info when call ends
        setCallerInfo(null);
      }
    };

    lookupCaller();
  }, [callContext.state, callContext.callerId]);

  // Ticket list query params
  const ticketQueryParams = useMemo(
    () => ({
      status: filters.status,
      priority: filters.priority,
      category: filters.category,
      search: filters.search,
      page: 1,
      pageSize: 20,
    }),
    [filters]
  );

  // Fetch tickets
  const {
    tickets,
    loading: ticketsLoading,
    total,
    page,
    pageSize,
    totalPages,
    refetch: refetchTickets,
    setPage,
  } = useTickets(ticketQueryParams);

  // Fetch selected ticket detail
  const {
    ticket: selectedTicket,
    loading: ticketLoading,
    refetch: refetchTicket,
    updateTicket,
  } = useTicketDetail(selectedTicketId);

  // Fetch messages for selected ticket
  const {
    messages,
    loading: messagesLoading,
    refetch: refetchMessages,
    addLocalMessage,
  } = useMessages(selectedTicketId, 5000);

  // Fetch calls for selected ticket
  const {
    calls,
    loading: callsLoading,
    refetch: refetchCalls,
  } = useCallHistory(selectedTicketId);

  // Ticket actions
  const {
    updateStatus,
    updatePriority,
    escalate,
    sendMessage,
  } = useTicketActions();

  // Handle ticket selection
  const handleSelectTicket = useCallback((ticketId: string) => {
    setSelectedTicketId(ticketId);
    setShowDetail(true);
  }, []);

  // Handle close detail (mobile)
  const handleCloseDetail = useCallback(() => {
    setShowDetail(false);
    setSelectedTicketId(null);
  }, []);

  // Handle filter change
  const handleFilterChange = useCallback((newFilters: TicketFilters) => {
    setFilters(newFilters);
  }, []);

  // Handle status update
  const handleUpdateStatus = useCallback(
    async (status: TicketStatus) => {
      if (!selectedTicketId) return false;
      const success = await updateStatus(selectedTicketId, status);
      if (success) {
        updateTicket({ status });
        refetchTickets();
      }
      return success;
    },
    [selectedTicketId, updateStatus, updateTicket, refetchTickets]
  );

  // Handle priority update
  const handleUpdatePriority = useCallback(
    async (priority: TicketPriority) => {
      if (!selectedTicketId) return false;
      const success = await updatePriority(selectedTicketId, priority);
      if (success) {
        updateTicket({ priority });
        refetchTickets();
      }
      return success;
    },
    [selectedTicketId, updatePriority, updateTicket, refetchTickets]
  );

  // Handle escalate
  const handleEscalate = useCallback(
    async (reason: string) => {
      if (!selectedTicketId) return false;
      const success = await escalate(selectedTicketId, reason);
      if (success) {
        updateTicket({
          escalationStatus: 'AGENT_REQUESTED_ADMIN_REVIEW' as any,
          priority: TicketPriority.URGENT,
        });
        refetchTickets();
      }
      return success;
    },
    [selectedTicketId, escalate, updateTicket, refetchTickets]
  );

  // Handle send message
  const handleSendMessage = useCallback(
    async (content: string, type: 'APP' | 'INTERNAL_NOTE') => {
      if (!selectedTicketId) return false;
      const message = await sendMessage(selectedTicketId, content, type);
      if (message) {
        addLocalMessage(message);
        // If it's a reply, update status to IN_PROGRESS if it's OPEN
        if (type === 'APP' && selectedTicket?.status === TicketStatus.OPEN) {
          await handleUpdateStatus(TicketStatus.IN_PROGRESS);
        }
        return true;
      }
      return false;
    },
    [selectedTicketId, sendMessage, addLocalMessage, selectedTicket, handleUpdateStatus]
  );

  // Handle make call
  const handleMakeCall = useCallback(() => {
    if (!selectedTicket?.user?.phoneNumber) {
      showToast('error', 'Customer has no phone number on file');
      return;
    }
    makeCall(selectedTicket.user.phoneNumber, selectedTicketId || undefined);
  }, [selectedTicket, selectedTicketId, makeCall]);

  // Handle incoming call accept
  const handleAcceptCall = useCallback(() => {
    acceptCall();
    // Optionally: could try to find related ticket and open it
  }, [acceptCall]);

  // Handle page change
  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
    },
    [setPage]
  );

  return (
    <div className="h-full flex">
      {/* Ticket List Panel */}
      <div
        className={`
          w-full lg:w-96 flex-shrink-0 flex-col
          ${showDetail ? 'hidden lg:flex' : 'flex'}
        `}
      >
        {/* Online Agents Indicator */}
        <div className="flex-shrink-0 p-4 border-b border-border">
          <OnlineAgentsIndicator
            agents={onlineAgents}
            currentAgentIdentity={agentIdentity}
          />
        </div>

        {/* Ticket List */}
        <div className="flex-1 overflow-hidden">
          <SupportTicketList
            tickets={tickets}
            loading={ticketsLoading}
            selectedTicketId={selectedTicketId}
            onSelectTicket={handleSelectTicket}
            onFilterChange={handleFilterChange}
            filters={filters}
            total={total}
            page={page}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      </div>

      {/* Ticket Detail Panel */}
      <div
        className={`
          flex-1 min-w-0
          ${showDetail ? 'flex' : 'hidden lg:flex'}
        `}
      >
        <SupportTicketDetail
          ticket={selectedTicket}
          loading={ticketLoading}
          messages={messages}
          messagesLoading={messagesLoading}
          calls={calls}
          callsLoading={callsLoading}
          callContext={callContext}
          isVoiceReady={isVoiceReady}
          isVoiceConfigured={isVoiceConfigured}
          onSendMessage={handleSendMessage}
          onUpdateStatus={handleUpdateStatus}
          onUpdatePriority={handleUpdatePriority}
          onEscalate={handleEscalate}
          onMakeCall={handleMakeCall}
          onHangup={hangup}
          onToggleMute={toggleMute}
          onClose={handleCloseDetail}
        />
      </div>

      {/* Incoming Call Modal */}
      {incomingCall && callContext.state === 'incoming' && (
        <IncomingCallModal
          callerId={callContext.callerId}
          callerName={callerInfo?.user?.name || undefined}
          callerInfo={callerInfo}
          callerLookupLoading={callerLookupLoading}
          onAccept={handleAcceptCall}
          onReject={rejectCall}
          onSelectTicket={(ticketId) => {
            setSelectedTicketId(ticketId);
            setShowDetail(true);
          }}
        />
      )}
    </div>
  );
};

export default SupportDashboard;
